import axios from 'axios'

import type { AuthUser } from '@/nordy/interfaces'

import { trackNordyCreditExhausted } from './gtm'
import { getOrCreateAttributionContext } from './mixpanelAttribution'
import { mixpanelRuntime } from './mixpanelRuntime'
import type { AttributionContext, DeviceCategory } from './mixpanelTypes'

export type CreditExhaustedPayload = {
  user_id: string
  current_user_plan: string
  run_type: 'comfy'
  location: string
  device_category: DeviceCategory
  flow_id?: string
  model?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
}

export type CreditExhaustedMixpanelPayload = Omit<
  CreditExhaustedPayload,
  'user_id'
>

export interface CreditExhaustedWorkflow {
  workflowId?: string
  workflowName?: string
  isPersisted?: boolean
}

export type CreditExhaustedGtmPayload = CreditExhaustedPayload & {
  event: 'credit_exhausted'
  page_slug: string
  current_url: string
}

interface BuildCreditExhaustedPayloadOptions {
  user: Pick<AuthUser, '_id' | 'subscriptionItemName'> | null
  currentUrl: URL
  workflow?: CreditExhaustedWorkflow | null
  attribution: Pick<AttributionContext, 'deviceCategory' | 'rawUtm'>
}

function trackFailOpen(track: () => void): void {
  try {
    track()
  } catch {
    return
  }
}

function getErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined

  const data = error.response?.data
  if (!data || typeof data !== 'object' || !('errorCode' in data)) {
    return undefined
  }

  return typeof data.errorCode === 'string' ? data.errorCode : undefined
}

export function buildCreditExhaustedPayload({
  user,
  currentUrl,
  workflow,
  attribution
}: BuildCreditExhaustedPayloadOptions): CreditExhaustedPayload | null {
  if (!user?._id) return null

  const payload: CreditExhaustedPayload = {
    user_id: user._id,
    current_user_plan:
      user.subscriptionItemName?.trim().toLowerCase() || 'free',
    run_type: 'comfy',
    location: currentUrl.pathname,
    device_category: attribution.deviceCategory
  }

  if (workflow?.isPersisted && workflow.workflowId) {
    payload.flow_id = workflow.workflowId
  }
  if (workflow?.workflowName) payload.model = workflow.workflowName

  const { rawUtm } = attribution
  if (rawUtm.utm_source) payload.utm_source = rawUtm.utm_source
  if (rawUtm.utm_medium) payload.utm_medium = rawUtm.utm_medium
  if (rawUtm.utm_campaign) payload.utm_campaign = rawUtm.utm_campaign
  if (rawUtm.utm_term) payload.utm_term = rawUtm.utm_term
  if (rawUtm.utm_content) payload.utm_content = rawUtm.utm_content

  return payload
}

export function buildCreditExhaustedGtmPayload(
  payload: CreditExhaustedPayload,
  currentUrl: URL
): CreditExhaustedGtmPayload {
  return {
    event: 'credit_exhausted',
    ...payload,
    page_slug: currentUrl.pathname,
    current_url: currentUrl.href
  }
}

export function isCreditExhaustedError(error: unknown): boolean {
  return getErrorCode(error) === 'CREDIT_NOT_ENOUGH'
}

export function trackCreditExhausted(
  user: Pick<AuthUser, '_id' | 'subscriptionItemName'> | null,
  workflow?: CreditExhaustedWorkflow | null
): void {
  try {
    const currentUrl = new URL(window.location.href)
    const attribution = getOrCreateAttributionContext({
      url: currentUrl,
      referrer: document.referrer
    })
    const payload = buildCreditExhaustedPayload({
      user,
      currentUrl,
      workflow,
      attribution
    })
    if (!payload) return

    const { user_id: _userId, ...mixpanelPayload } = payload
    trackFailOpen(() => mixpanelRuntime.events.creditExhausted(mixpanelPayload))
    trackFailOpen(() =>
      trackNordyCreditExhausted(
        buildCreditExhaustedGtmPayload(payload, currentUrl)
      )
    )
  } catch {
    return
  }
}
